import { useEffect, useMemo, useState } from "react";
import classes from "./StatusEditor.module.css";
import { useStudioEditor } from "../../../store/studioEditorStore";
import { useArchiveStudio } from "../../../hooks/useArchiveStudio";
import { SmallModal } from "../../UI/SmallModal/SmallModal";

const ACTIVE_STATUS = "ACTIVE";
const INACTIVE_STATUS = "INACTIVE";

const getInitialStatus = (studioStatus) => {
    if (studioStatus === INACTIVE_STATUS) return INACTIVE_STATUS;
    return ACTIVE_STATUS;
};

export const StatusEditor = ({ validate, studio }) => {
    const { updateField } = useStudioEditor();
    const { checkForFutureBookings, fetchingStudio } = useArchiveStudio(studio?.id);
    const initialStatus = useMemo(() => getInitialStatus(studio?.status), [studio?.status]);
    const [selectedStatus, setSelectedStatus] = useState(initialStatus);
    const [showBookingModal, setShowBookingModal] = useState(false);

    useEffect(() => {
        const nextStatus = getInitialStatus(studio?.status);
        setSelectedStatus(nextStatus);
    }, [studio?.status]);

    const revertToCurrentStatus = () => {
        const currentStatus = getInitialStatus(studio?.status);
        setSelectedStatus(currentStatus);
        updateField("status", currentStatus);
    };

    const hasActiveBookings = () => {
        const bookingCheck = checkForFutureBookings(studio?.id);
        return Boolean(bookingCheck?.hasFutureBookings);
    };

    const handleBookingConflict = () => {
        setShowBookingModal(true);
        if (selectedStatus === INACTIVE_STATUS) {
            revertToCurrentStatus();
        }
    };

    useEffect(() => {
        const handleValidation = () => {
            if (selectedStatus !== INACTIVE_STATUS) {
                return true;
            }

            if (fetchingStudio) {
                return false;
            }

            if (hasActiveBookings()) {
                handleBookingConflict();
                return false;
            }

            return true;
        };

        validate.current = handleValidation;
        validate.showBookingConflict = handleBookingConflict;
    }, [validate, selectedStatus, studio?.id, studio?.status, fetchingStudio]);

    const handleSelect = (statusValue) => {
        if (statusValue === INACTIVE_STATUS) {
            if (fetchingStudio) {
                return;
            }

            if (hasActiveBookings()) {
                handleBookingConflict();
                return;
            }
        }

        setSelectedStatus(statusValue);
        updateField("status", statusValue);
    };

    const handleCloseBookingModal = () => {
        setShowBookingModal(false);
    };

    return (
        <>
            <div className={classes.mainContainer}>
                <h2 className={`${classes.title} titleSemibold2`}>Studio Status</h2>

                <button
                    type="button"
                    className={`${classes.statusCard} ${selectedStatus === ACTIVE_STATUS ? classes.activeCard : ""}`}
                    onClick={() => handleSelect(ACTIVE_STATUS)}
                >
                    <div className={classes.statusHeader}>
                        <div className={`${classes.radioCircle} ${selectedStatus === ACTIVE_STATUS ? classes.selectedCircle : ""}`}>
                            {selectedStatus === ACTIVE_STATUS && 
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <rect width="24" height="24" rx="12" fill="#1F1F1F"/>
                                    <line x1="6.68079" y1="11.8192" x2="10.2545" y2="15.4697" stroke="white" strokeWidth="1.55556"/>
                                    <line x1="9.16709" y1="15.4521" x2="17.5924" y2="7.43826" stroke="white" strokeWidth="1.55556"/>
                                </svg>
                            }
                        </div>
                        <span className={`${classes.statusLabel} titleSemibold3`}>Aktiv</span>
                    </div>
                    <p className={`${classes.statusText}`}>
                        Dein Studio ist in den Suchergebnissen sichtbar und kann von Kreativen gebucht werden.
                    </p>
                </button>

                <button
                    type="button"
                    className={`${classes.statusCard} ${selectedStatus === INACTIVE_STATUS ? classes.activeCard : ""}`}
                    onClick={() => handleSelect(INACTIVE_STATUS)}
                >
                    <div className={classes.statusHeader}>
                        <div className={`${classes.radioCircle} ${selectedStatus === INACTIVE_STATUS ? classes.selectedCircle : ""}`}>
                            {selectedStatus === INACTIVE_STATUS && <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <rect width="24" height="24" rx="12" fill="#1F1F1F"/>
                                <line x1="6.68079" y1="11.8192" x2="10.2545" y2="15.4697" stroke="white" strokeWidth="1.55556"/>
                                <line x1="9.16709" y1="15.4521" x2="17.5924" y2="7.43826" stroke="white" strokeWidth="1.55556"/>
                            </svg>}
                        </div>
                        <span className={`${classes.statusLabel} titleSemibold3 `}>Inaktiv</span>
                    </div>
                    <p className={`${classes.statusText} regularMiddle`}>
                        Dein Studio ist nicht buchbar und wird nicht in den Suchergebnissen angezeigt.
                    </p>
                    <p className={`${classes.statusText} regularMiddle`}>
                        Du kannst es jederzeit wieder aktivieren. Aktive Buchungen bleiben bestehen.
                    </p>
                </button>
            </div>

            <SmallModal
                open={showBookingModal}
                onBackdropClick={handleCloseBookingModal}
                fromDeleteStudio={true}
                className={classes.bookingConflictModal}
            >
                <div className={classes.bookingConflictContent}>
                    <button
                        type="button"
                        className={classes.bookingConflictCloseButton}
                        onClick={handleCloseBookingModal}
                        aria-label="Schließen"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M7.56773 8.68115L0.5 1.61342L1.43227 0.681152L8.5 7.74888L15.5677 0.681152L16.5 1.61342L9.43227 8.68115L16.5 15.7489L15.5677 16.6812L8.5 9.61342L1.43227 16.6812L0.5 15.7489L7.56773 8.68115Z" fill="#1F1F1F"/>
                        </svg>
                    </button>

                    <h2 className={`${classes.bookingConflictTitle} titleSemibold3`}>
                        Du hast noch aktive Buchungen.
                    </h2>
                    <p className={`${classes.bookingConflictText} regularMiddle`}>
                        Dein Studio ist deaktiviert. Bestehende Buchungen bleiben bestehen und müssen bei Bedarf manuell storniert werden.
                    </p>

                    <button
                        type="button"
                        className={`${classes.bookingConflictButton} mediumLarge`}
                        onClick={handleCloseBookingModal}
                    >
                        Fertig
                    </button>
                </div>
            </SmallModal>
        </>
    );
};

export default StatusEditor;
